import { makeAutoObservable, action, computed, reaction } from '../../common/stores/mobx-config.js';
import timelineEventsStore from './timelineEventsStore.js';
import gedcomDataStore from '../../gedcom/stores/gedcomDataStore.js';
import { storeEvents, EVENTS } from '../../common/stores/storeEvents.js';
import { logger } from '../../utils/debug/logger.js';
import { getSurname } from '../../gedcom/formatters/individualFormatters.js';
import { getBranchFromSosa } from '../../common/utils/branchUtils.js';

/**
 * Passive store for timeline filters.
 * Holds filter state and computes filtered events.
 * Does NOT trigger timeline rebuilds — timelineStore reacts to filter changes.
 */
class TimelineFiltersStore {
    filters = {
        eventTypes: {
            birth: true,
            death: true,
            marriage: true
        },
        branch: 'all',
        surname: '',
        generation: {
            min: 0,
            max: 10
        },
        location: '',
        showHistoricalEvents: true,
        historicalScope: 'all',
        historicalCategory: 'all'
    };

    _availableSurnames = [];
    _maxGeneration = 10;
    _availableLocations = [];

    constructor() {
        makeAutoObservable(this, {
            setEventTypeFilter: action,
            setBranchFilter: action,
            setSurnameFilter: action,
            setGenerationFilter: action,
            setLocationFilter: action,
            setShowHistoricalEvents: action,
            setHistoricalScope: action,
            setHistoricalCategory: action,
            resetFilters: action,
            rebuildFilterOptions: action,
            filteredEvents: computed,
            hasActiveFilters: computed
        });

        this.loadFromLocalStorage();

        // Persist filters to localStorage on any change
        this.persistReactionDisposer = reaction(
            () => [
                this.filters.eventTypes,
                this.filters.branch,
                this.filters.surname,
                this.filters.generation,
                this.filters.location,
                this.filters.showHistoricalEvents,
                this.filters.historicalScope,
                this.filters.historicalCategory
            ],
            () => {
                this.saveToLocalStorage();
            }
        );

        // Reset filters on new GEDCOM import
        storeEvents.subscribe(EVENTS.GEDCOM.IMPORT_STARTED, () => {
            logger.timeline.filters.info('New GEDCOM import detected, resetting filters');
            this.resetFilters();
        });
    }

    /**
     * Rebuilds available filter options from current events.
     * Called once when timeline population completes (not per-event).
     */
    rebuildFilterOptions() {
        const events = timelineEventsStore.events;

        const surnames = new Set();
        const locations = new Set();
        let maxGen = 0;

        for (const event of events) {
            // Surnames
            const person = gedcomDataStore.getIndividual(event.personId);
            if (person) {
                const surname = getSurname(person);
                if (surname) surnames.add(surname);
            }

            // Generations
            if (event.sosa) {
                const info = getBranchFromSosa(event.sosa);
                if (info.generation > maxGen) maxGen = info.generation;
            }

            // Locations
            if (event.town) locations.add(event.town);
            if (event.townDisplay && event.townDisplay !== event.town) {
                locations.add(event.townDisplay);
            }
        }

        this._availableSurnames = Array.from(surnames).sort();
        this._maxGeneration = Math.max(maxGen, 1);
        this._availableLocations = Array.from(locations).sort();

        logger.timeline.filters.info(
            `Filter options rebuilt: ${this._availableSurnames.length} surnames, ` +
            `${this._maxGeneration} max gen, ${this._availableLocations.length} locations`
        );
    }

    saveToLocalStorage() {
        try {
            localStorage.setItem('timelineFilters', JSON.stringify(this.filters));
        } catch (e) {
            logger.timeline.filters.warn('Could not save timeline filters to localStorage:', e);
        }
    }

    loadFromLocalStorage() {
        try {
            const saved = localStorage.getItem('timelineFilters');
            if (saved) {
                this.filters = JSON.parse(saved);
                logger.timeline.filters.info('Loaded filters from localStorage');
            }
        } catch (e) {
            logger.timeline.filters.warn('Could not load timeline filters from localStorage:', e);
        }
    }

    get hasActiveFilters() {
        return (
            !this.filters.eventTypes.birth ||
            !this.filters.eventTypes.death ||
            !this.filters.eventTypes.marriage ||
            this.filters.branch !== 'all' ||
            this.filters.surname !== '' ||
            this.filters.generation.min > 0 ||
            this.filters.generation.max < this._maxGeneration ||
            this.filters.location !== '' ||
            !this.filters.showHistoricalEvents ||
            this.filters.historicalScope !== 'all' ||
            this.filters.historicalCategory !== 'all'
        );
    }

    get filterOptions() {
        return {
            surnames: this._availableSurnames,
            maxGeneration: this._maxGeneration,
            locations: this._availableLocations
        };
    }

    /**
     * Returns events filtered by all active criteria.
     * Single-pass filter with combined predicate for performance.
     */
    get filteredEvents() {
        const allEvents = timelineEventsStore.events;
        if (!this.hasActiveFilters) return allEvents;

        // Pre-compute filter predicates
        const { eventTypes, branch, surname, generation, location } = this.filters;

        const filterByType = !eventTypes.birth || !eventTypes.death || !eventTypes.marriage;
        const activeTypes = filterByType
            ? new Set(
                [
                    eventTypes.birth && 'birth',
                    eventTypes.death && 'death',
                    eventTypes.marriage && 'marriage'
                ].filter(Boolean)
            )
            : null;

        const filterByBranch = branch !== 'all';
        const filterBySurname = surname !== '';
        const filterByGeneration = generation.min > 0 || generation.max < this._maxGeneration;
        const filterByLocation = location !== '';
        const locationLower = filterByLocation ? location.toLowerCase() : '';

        try {
            const result = allEvents.filter(event => {
                // Event type
                if (activeTypes && !activeTypes.has(event.type)) return false;

                // Branch + generation (single getBranchFromSosa call)
                if ((filterByBranch || filterByGeneration) && event.sosa) {
                    const info = getBranchFromSosa(event.sosa);
                    if (filterByBranch && info.branch !== branch) return false;
                    if (filterByGeneration) {
                        if (info.generation < generation.min || info.generation > generation.max) return false;
                    }
                }

                // Surname
                if (filterBySurname) {
                    const person = gedcomDataStore.getIndividual(event.personId);
                    if (!person || getSurname(person) !== surname) return false;
                }

                // Location
                if (filterByLocation) {
                    const townMatch = event.town && event.town.toLowerCase().includes(locationLower);
                    const displayMatch = event.townDisplay && event.townDisplay.toLowerCase().includes(locationLower);
                    if (!townMatch && !displayMatch) return false;
                }

                return true;
            });

            logger.timeline.filters.info(`Filtered events: ${result.length}/${allEvents.length}`);
            return result;

        } catch (e) {
            logger.timeline.filters.error('Error filtering events:', e);
            return allEvents;
        }
    }

    setEventTypeFilter(type, value) {
        if (this.filters.eventTypes[type] !== undefined) {
            this.filters.eventTypes[type] = value;
            logger.timeline.filters.debug(`Event type filter "${type}" set to ${value}`);
        }
    }

    setBranchFilter(branch) {
        if (['all', 'paternal', 'maternal'].includes(branch)) {
            this.filters.branch = branch;
            logger.timeline.filters.debug(`Branch filter set to "${branch}"`);
        }
    }

    setSurnameFilter(surname) {
        this.filters.surname = surname || '';
    }

    setGenerationFilter(min, max) {
        this.filters.generation.min = min !== undefined ? min : this.filters.generation.min;
        this.filters.generation.max = max !== undefined ? max : this.filters.generation.max;
    }

    setLocationFilter(location) {
        this.filters.location = location || '';
    }

    setShowHistoricalEvents(value) {
        this.filters.showHistoricalEvents = !!value;
    }

    setHistoricalScope(scope) {
        if (['all', 'france', 'europe', 'mondial'].includes(scope)) {
            this.filters.historicalScope = scope;
        }
    }

    setHistoricalCategory(category) {
        this.filters.historicalCategory = category || 'all';
    }

    resetFilters() {
        this.filters = {
            eventTypes: { birth: true, death: true, marriage: true },
            branch: 'all',
            surname: '',
            generation: { min: 0, max: this._maxGeneration },
            location: '',
            showHistoricalEvents: true,
            historicalScope: 'all',
            historicalCategory: 'all'
        };
        logger.timeline.filters.debug('All filters reset to default values');
    }

    dispose() {
        if (this.persistReactionDisposer) {
            this.persistReactionDisposer();
        }
    }
}

const timelineFiltersStore = new TimelineFiltersStore();
export default timelineFiltersStore;
